import { ActivityFilters } from 'containers/CRM/crmActivity/ActivityFilters/ActivityFilters';
import { ActivityFiltersAndButtons } from 'containers/CRM/crmActivity/ActivityFilters/ActivityFiltersAndButtons';
import { ActivityList } from 'containers/CRM/crmActivity/ActivityList/ActivityList';
import { useActivities } from 'containers/CRM/hooks/useActivities';
import { useActivityFilters } from 'containers/CRM/hooks/useActivityFilters';
import React, { useCallback, useEffect, useState } from 'react';
import { ICrmActivity } from 'services/CrmApi/types/CrmActivityTypes';
import { ECrmDirectoryEntryType } from 'services/CrmApi/types/DirectoryEntry';
import { VerticalSpace } from 'ui/VerticalSpace';
import qs from 'qs';
import { getReferencedActivityIdFromUrl } from 'containers/CRM/helpers';
import { useRedirectIfActivityDoesNotExist } from 'containers/CRM/hooks/useRedirectIfActivityDoesNotExist';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';

interface ICrmCompanyActivityProps {
  directoryEntryId: string;
}

export const CrmCompanyActivity: React.FC<ICrmCompanyActivityProps> = ({ directoryEntryId }) => {
  const { dynamicParameters } = useDynamicParameters();
  const {
    activityFilters,
    dispatchActivityFilters,
    resetHighlightedActivity,
    highlightedActivityId,
    setHighlightedActivityId,
    handleSortOrderChange,
  } = useActivityFilters(dynamicParameters.ENABLE_CRM_TMS);
  const {
    activities,
    activitiesCount,
    handleLoadMore,
    crmActivities,
  } = useActivities(directoryEntryId, activityFilters, dispatchActivityFilters);

  const refetchActivities = useCallback(() => {
    crmActivities.refetch();
  }, [crmActivities]);

  const handleCreateActivity = useCallback(() => {
    resetHighlightedActivity();
    setHighlightedActivityId(null);
    crmActivities.refetch();
  }, [resetHighlightedActivity, crmActivities]);

  const handleEditActivity = useCallback(
    async (activity?: ICrmActivity) => {
      if (activity) {
        window.history.pushState({}, '', `?key=${activity.id}`);
        dispatchActivityFilters({ type: 'set_cursor', value: activity.id });
        if (highlightedActivityId && highlightedActivityId === activity.id) {
          await crmActivities.refetch();
        }
        setHighlightedActivityId(activity.id);
      }
    },
    [crmActivities]
  );

  useEffect(() => {
    const referencedActivityId = getReferencedActivityIdFromUrl();
    if (referencedActivityId) {
      setHighlightedActivityId(referencedActivityId);
    }
  }, []);

  useEffect(() => {
    if (crmActivities.isError) {
      resetHighlightedActivity();
    }
  }, [crmActivities.isError, resetHighlightedActivity]);

  return (
    <div className="w-full">
      <ActivityFiltersAndButtons
        directoryEntryId={directoryEntryId}
        directoryEntryType={ECrmDirectoryEntryType.COMPANY}
        activityFilters={activityFilters}
        dispatchActivityFilters={dispatchActivityFilters}
        activityFiltersDisabled={crmActivities.isFetching || crmActivities.isFetchingNextPage}
        onCreateActivity={handleCreateActivity}
        resetHighlightedActivity={resetHighlightedActivity}
      />
      <VerticalSpace height="20px" />
      <ActivityList
        activities={activities ?? []}
        count={activitiesCount}
        highlightedActivityId={highlightedActivityId}
        onLoadMore={handleLoadMore}
        isLoading={crmActivities.isFetching}
        isFetchingNextPage={crmActivities.isFetchingNextPage}
        sortOrder={activityFilters.sort.order}
        onSortOrderChange={handleSortOrderChange}
        onEditActivity={handleEditActivity}
        onDeleteActivity={refetchActivities}
        directoryEntryId={directoryEntryId}
        directoryEntryType={ECrmDirectoryEntryType.COMPANY}
      />
    </div>
  );
};
